import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Stack, TextField, Typography } from '@mui/material';
import { EventNote } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import eventCategoryService, { EventCategory } from '../../services/eventCategoryService';
import SettingsList, { SettingsTile } from '../common/SettingsList';
import SettingsDialog from '../common/SettingsDialog';
import ColorSwatchPicker, { swatchAt } from '../common/ColorSwatchPicker';

const EventCategorySettings: React.FC = () => {
  const { canWrite } = useModulePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const writable = canWrite('settings_event_categories');

  const [categories, setCategories] = useState<EventCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<EventCategory | null>(null);
  const [form, setForm] = useState({ name: '', description: '', color: swatchAt(0) });
  const [saving, setSaving] = useState(false);
  // Categories created since the last load, so each one in a batch gets its own colour.
  const addedSinceLoad = useRef(0);

  const load = useCallback(async () => {
    try {
      setCategories(await eventCategoryService.getCategories());
      addedSinceLoad.current = 0;
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Could not load event categories', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const create = (name: string) =>
    eventCategoryService.createCategory({ name, color: swatchAt(categories.length + addedSinceLoad.current++) });

  const edit = (c: EventCategory) => {
    setEditing(c);
    setForm({ name: c.name, description: c.description ?? '', color: c.color || swatchAt(0) });
  };

  const save = async () => {
    if (!editing || !form.name.trim()) return;
    setSaving(true);
    try {
      await eventCategoryService.updateCategory(editing._id, { ...form, name: form.name.trim() });
      enqueueSnackbar('Category saved', { variant: 'success' });
      setEditing(null);
      load();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Could not save this category', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SettingsList
        rows={categories}
        loading={loading}
        rowKey={c => c._id}
        name={c => c.name}
        subtitle={c => c.description || undefined}
        leading={c => <SettingsTile color={c.color}><EventNote /></SettingsTile>}
        searchText={c => `${c.name} ${c.description ?? ''}`}
        canWrite={writable}
        onEdit={edit}
        remove={{
          run: c => eventCategoryService.deleteCategory(c._id),
          noun: 'category',
          consequence: () => 'It will no longer be offered when creating events. A category that events still use cannot be deleted — move those events first.',
        }}
        quickAdd={{
          noun: { one: 'category', many: 'categories' },
          placeholder: 'Add categories, e.g. Academic, Sports, Cultural',
          create,
          presets: [
            { label: 'School calendar', names: ['Academic', 'Sports', 'Cultural', 'Religious', 'Parent-Teacher'] },
          ],
        }}
        reload={load}
        empty={{
          icon: <EventNote />,
          title: 'No event categories yet',
          description: 'Categories group and colour-code events. Add them above — several at once if you like.',
        }}
      />

      <SettingsDialog
        open={!!editing}
        title="Edit category"
        onClose={() => setEditing(null)}
        onSave={save}
        saving={saving}
        saveDisabled={!form.name.trim()}
      >
        <TextField
          label="Name"
          value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          error={!form.name.trim()}
          helperText={!form.name.trim() ? 'A category needs a name' : ' '}
          inputProps={{ maxLength: 100 }}
          fullWidth
        />
        <TextField
          label="Description"
          value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          placeholder="Optional"
          inputProps={{ maxLength: 500 }}
          multiline
          minRows={2}
          fullWidth
        />
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">Colour</Typography>
          <ColorSwatchPicker value={form.color} onChange={color => setForm(f => ({ ...f, color }))} />
        </Stack>
      </SettingsDialog>
    </>
  );
};

export default EventCategorySettings;
